import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Button,
  cn,
  EmptyState,
  Flex,
  LoadingOrbit,
  Page,
  PageHeader,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import { TestTube } from '@phosphor-icons/react';
import type { ExternalTestHealth, ExternalTestProvider } from '@chatsdet/types';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useExternalTestCatalog,
  useExternalTestCatalogRecentRuns,
  type ExternalTestCatalogQuery,
  type ExternalTestCatalogRecentRun,
  type ExternalTestCatalogSortBy,
} from '../../features/external-tests/useExternalTestCatalog';
import { ExternalTestsCatalogFilterBar } from './ExternalTestsCatalog/ExternalTestsCatalogFilterBar';
import { ExternalTestsCatalogTable } from './ExternalTestsCatalog/ExternalTestsCatalogTable';

// Stable reference so an empty recent-runs response doesn't create a new
// object identity every render, which would otherwise bust the table's
// columns useMemo on each re-render.
const EMPTY_RECENT_RUNS: Record<string, ExternalTestCatalogRecentRun[]> = Object.freeze({});

const PAGE_SIZE = 25;
const PROVIDERS: MultiSelectOption[] = [
  { id: 'playwright', label: 'Playwright' },
  { id: 'selenium', label: 'Selenium' },
  { id: 'cypress', label: 'Cypress' },
  { id: 'junit', label: 'JUnit' },
];
const HEALTH: MultiSelectOption[] = [
  { id: 'passing', label: 'Passing' },
  { id: 'flaky', label: 'Flaky' },
  { id: 'failing', label: 'Failing' },
  { id: 'unverified', label: 'Unverified' },
];
const HEALTH_LABELS: Record<string, string> = Object.fromEntries(
  HEALTH.map((item) => [item.id, item.label])
);
type CatalogSearch = Omit<ExternalTestCatalogQuery, 'page' | 'pageSize'> & {
  page?: number;
  pageSize?: number;
};

export function ExternalTestsCatalog() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/runs/external-tests' }) as CatalogSearch;
  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? PAGE_SIZE;
  const [searchInput, setSearchInput] = React.useState(search.search ?? '');
  const query = useExternalTestCatalog({ ...search, page, pageSize });
  const rows = query.data?.data.tests ?? [];
  const pagination = query.data?.pagination;
  const rowIds = React.useMemo(() => rows.map((row) => row.id), [rows]);
  const recentRunsQuery = useExternalTestCatalogRecentRuns(rowIds);
  const recentRunsByTest = recentRunsQuery.data ?? EMPTY_RECENT_RUNS;
  const suites = query.data?.data.suites ?? [];
  const sources = query.data?.data.sources ?? [];

  const isOutOfRangePage =
    !query.isLoading &&
    !query.isError &&
    !query.isPlaceholderData &&
    pagination !== undefined &&
    pagination.totalPages > 0 &&
    page > pagination.totalPages;
  const isPageResponsePending =
    !query.isError && query.isPlaceholderData && query.isFetching && pagination?.page !== page;
  const isCorrectingPage = isOutOfRangePage || isPageResponsePending;

  useBreadcrumbs([
    { key: 'suites', label: 'Suites', href: '/runs/test' },
    { key: 'external-tests', label: 'External Tests' },
  ]);

  const updateSearch = React.useCallback(
    (updates: Partial<CatalogSearch>, options?: { replace?: boolean }) =>
      navigate({
        to: '/runs/external-tests',
        replace: options?.replace,
        search: (previous: Record<string, unknown>) => {
          const next = { ...previous, ...updates } as CatalogSearch;
          if (!next.search) delete next.search;
          if (!next.suiteIds?.length) delete next.suiteIds;
          if (!next.sourceKeys?.length) delete next.sourceKeys;
          if (!next.providers?.length) delete next.providers;
          if (!next.health?.length) delete next.health;
          if (!next.sortBy || next.sortBy === 'lastSeenAt') delete next.sortBy;
          if (!next.sortOrder || next.sortOrder === 'desc') delete next.sortOrder;
          if (next.page === 1) delete next.page;
          if (next.pageSize === PAGE_SIZE) delete next.pageSize;
          return next;
        },
      }),
    [navigate]
  );

  React.useEffect(() => setSearchInput(search.search ?? ''), [search.search]);
  React.useEffect(() => {
    if (!isOutOfRangePage || !pagination) return;
    void updateSearch({ page: pagination.totalPages }, { replace: true });
  }, [isOutOfRangePage, pagination, updateSearch]);
  React.useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (search.search ?? '')) return;
    const timer = window.setTimeout(
      () => updateSearch({ search: trimmed || undefined, page: 1 }),
      300
    );
    return () => window.clearTimeout(timer);
  }, [searchInput, search.search, updateSearch]);

  const suiteOptions = React.useMemo<MultiSelectOption[]>(
    () =>
      suites.map((suite) => ({
        id: suite.id,
        label: suite.archived ? `${suite.name} (archived)` : suite.name,
      })),
    [suites]
  );
  const sourceOptions = React.useMemo<MultiSelectOption[]>(
    () => sources.map((source) => ({ id: source.sourceKey, label: source.name })),
    [sources]
  );
  const chips = React.useMemo<FilterToolbarChip[]>(() => {
    const result: FilterToolbarChip[] = [];
    if (search.search) result.push({ id: 'search', label: `Search: ${search.search}` });
    search.suiteIds?.forEach((id) =>
      result.push({
        id: `suite:${id}`,
        label: `Suite: ${suites.find((suite) => suite.id === id)?.name ?? id}`,
      })
    );
    search.sourceKeys?.forEach((id) =>
      result.push({
        id: `source:${id}`,
        label: `Source: ${sourceOptions.find((source) => source.id === id)?.label ?? id}`,
      })
    );
    search.providers?.forEach((id) =>
      result.push({
        id: `provider:${id}`,
        label: `Provider: ${PROVIDERS.find((provider) => provider.id === id)?.label ?? id}`,
      })
    );
    search.health?.forEach((id) =>
      result.push({ id: `health:${id}`, label: `Health: ${HEALTH_LABELS[id] ?? id}` })
    );
    return result;
  }, [search, sourceOptions, suites]);

  const clearFilters = React.useCallback(() => {
    setSearchInput('');
    updateSearch({
      search: undefined,
      suiteIds: undefined,
      sourceKeys: undefined,
      providers: undefined,
      health: undefined,
      page: 1,
    });
  }, [updateSearch]);
  const removeChip = React.useCallback(
    (id: string) => {
      if (id === 'search') {
        setSearchInput('');
        updateSearch({ search: undefined, page: 1 });
        return;
      }
      const [kind, value] = id.split(':', 2);
      const key =
        kind === 'suite'
          ? 'suiteIds'
          : kind === 'source'
            ? 'sourceKeys'
            : kind === 'provider'
              ? 'providers'
              : 'health';
      const current = (search[key] ?? []) as string[];
      const next = current.filter((item) => item !== value);
      updateSearch({ [key]: next.length ? next : undefined, page: 1 });
    },
    [search, updateSearch]
  );

  // The toolbar fuses with the table card only when the table actually
  // renders — loading/empty/error states keep their own standalone chrome.
  const isTableLockup =
    !query.isLoading && !isCorrectingPage && !query.isError && rows.length > 0;

  const content =
    query.isLoading || isCorrectingPage ? (
      <Flex justify="center" className="py-24">
        <LoadingOrbit />
      </Flex>
    ) : query.isError ? (
      <EmptyState
        title="Unable to load external tests"
        description="Please try again or refresh the page."
        action={
          <Button variant="outline" size="sm" onPress={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    ) : rows.length === 0 && (pagination?.totalItems ?? 0) === 0 ? (
      <EmptyState
        title={chips.length ? 'No external tests match these filters' : 'No external tests yet'}
        description={
          chips.length
            ? 'Try a different search or clear the filters.'
            : 'External tests appear here after a suite reports its first run.'
        }
        action={
          chips.length ? (
            <Button variant="outline" size="sm" onPress={clearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
      />
    ) : (
      <ExternalTestsCatalogTable
        rows={rows}
        recentRunsByTest={recentRunsByTest}
        isRecentRunsLoading={recentRunsQuery.isLoading}
        isRecentRunsError={recentRunsQuery.isError}
        pagination={pagination}
        isLoading={query.isFetching}
        sortBy={search.sortBy}
        sortOrder={search.sortOrder}
        onPageChange={(nextPage) => updateSearch({ page: nextPage })}
        onPageSizeChange={(nextSize) => updateSearch({ page: 1, pageSize: nextSize })}
        onSortChange={(sortBy, sortOrder) =>
          updateSearch({
            sortBy: sortBy as ExternalTestCatalogSortBy | undefined,
            sortOrder,
            page: 1,
          })
        }
        // [&_table]:[table-layout:fixed] — scoped to this page only: the
        // unconstrained "Test" column next to long, un-wrapped test names
        // (up to ~190 chars) inflates the table's default auto-layout width
        // past its container, forcing horizontal scroll. Applied unconditionally
        // (not just when locked up) so it holds in the loading/empty states too.
        containerClassName={cn(
          '[&_table]:[table-layout:fixed]',
          isTableLockup && 'rounded-t-none filter-lockup-table'
        )}
      />
    );

  return (
    <Page>
      <PageHeader
        icon={<TestTube size={24} />}
        title="External Tests"
        description="Browse discovered tests across your external suites."
      />
      {/* One wrapper around the toolbar + body so ancestor space-y stacks
          can't inject a gap inside the lockup. */}
      <div className="mt-6">
        <ExternalTestsCatalogFilterBar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          suiteOptions={suiteOptions}
          sourceOptions={sourceOptions}
          providerOptions={PROVIDERS}
          healthOptions={HEALTH}
          suiteIds={search.suiteIds}
          sourceKeys={search.sourceKeys}
          providers={search.providers as ExternalTestProvider[] | undefined}
          health={search.health as ExternalTestHealth[] | undefined}
          chips={chips}
          onChange={(updates) => updateSearch({ ...updates, page: 1 })}
          onClear={clearFilters}
          onRemove={removeChip}
          attached={isTableLockup}
        />
        <div className={isTableLockup ? undefined : 'mt-3'}>{content}</div>
      </div>
    </Page>
  );
}
